import React, { useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  authApi,
  NotificationAttributes,
  NotificationPayloadBase,
  NotificationPlatformPayload
} from "@moodys/cre-cpm-client-apis.apis.services.auth"
import {
  REACT_SHARED_NAMESPACE,
  withLanguage,
  WithLanguageProps
} from "@moodys/cre-cpm-platform-shared.components.with-language"
import {
  ASSETS_LIST_EXPORT_NOTIFICATION_ID,
  DEAL_STATUS_CHANGE_NOTIFICATION_ID,
  LOANS_STRESS_REPORT_NOTIFICATION_ID,
  NOTIFICATION_MARGIN_TOP
} from "@moodys/cre-cpm-platform-shared.constants.notifications"
import { getBaseLocation } from "@moodys/cre-cpm-platform-shared.functions.navigation"
import { dateWithTimeLocaleFormats } from "@moodys/cre-cpm.constants.date-and-time"
import { VerticalSlicedMicroFrontendRoutes } from "@moodys/cre-cpm.constants.micro-frontends"
import EVENTS from "@moodys/cre-cpm.constants.events.notifications/dist/notifications"
import { Button, Col, notification, Row, Space } from "antd"
import moment from "moment"
import { useCookies } from "react-cookie"
import { useHistory } from "react-router-dom"
import PubSub from "pubsub-js"

interface WithNotificationsProps extends WithLanguageProps {
  children: React.ReactNode
  userId: string
  onClickCallback?: (response: NotificationAttributes) => void
  onCloseCallback?: (response: NotificationAttributes) => void
}

const clickableNotifications = [
  "deal_comment_created",
  "loan_comment_created",
  DEAL_STATUS_CHANGE_NOTIFICATION_ID,
  LOANS_STRESS_REPORT_NOTIFICATION_ID
]

const WithNotifications: React.FC<WithNotificationsProps> = ({
  children,
  language,
  onClickCallback = (): void => {},
  onCloseCallback = (): void => {},
  userId,
  i18nInstance
}) => {
  const [cookies] = useCookies(["rxd_auth_session"])
  const history = useHistory()
  const { t } = useTranslation(REACT_SHARED_NAMESPACE, { i18n: i18nInstance })

  const navigateTo = useCallback(
    (base: VerticalSlicedMicroFrontendRoutes, route: string) => {
      history.push(getBaseLocation(base, route))
    },
    [history]
  )

  const notificationMessage = (response: NotificationAttributes): string => {
    if (response.template_id === ASSETS_LIST_EXPORT_NOTIFICATION_ID) {
      return moment()
        .utc()
        .local()
        .format(dateWithTimeLocaleFormats[language || "en"])
    }

    if (response.payload && (response.payload as NotificationPlatformPayload).title) {
      return (response.payload as NotificationPlatformPayload).title!
    }

    if (response.payload && (response.payload as NotificationPayloadBase).alert_name) {
      return (response.payload as NotificationPayloadBase).alert_name
    }

    return t(
      `notifications.template.${response.template_id}.title`,
      (response.payload as unknown as {}) || {}
    )
  }

  useEffect(() => {
    const onReceived = (response: NotificationAttributes): void => {
      PubSub.publish(EVENTS.MA_CRE_CPM_NOTIFICATION_RECEIVED, response)
      const onLinkClick = (): void => {
        PubSub.publish(EVENTS.MA_CRE_CPM_NOTIFICATION_READ, response)
        onClickCallback(response)
        if (response.payload) {
          if ((response.payload as NotificationPlatformPayload).link) {
            window.location.href = (response.payload as NotificationPlatformPayload).link!
          } else if (response.template_id === "deal_comment_created") {
            navigateTo(
              VerticalSlicedMicroFrontendRoutes.platform,
              `/deals/details/${(response.payload as NotificationPlatformPayload).commented_entity_id}#comments`
            )
          } else if (response.template_id === "loan_comment_created") {
            navigateTo(
              VerticalSlicedMicroFrontendRoutes.loans,
              `/attributes/${(response.payload as NotificationPlatformPayload).commented_entity_id}`
            )
          } else if (response.template_id === DEAL_STATUS_CHANGE_NOTIFICATION_ID) {
            navigateTo(
              VerticalSlicedMicroFrontendRoutes.platform,
              `/deals/details/${(response.payload as NotificationPlatformPayload).deal_id}`
            )
            // Deal status change
            response.payload = {
              ...response.payload,
              new_status: t(
                `deal.status.${(response.payload as NotificationPlatformPayload).new_status}`
              ),
              previous_status: t(
                `deal.status.${(response.payload as NotificationPlatformPayload).previous_status}`
              )
            }
          } else if (response.template_id === LOANS_STRESS_REPORT_NOTIFICATION_ID) {
            navigateTo(
              VerticalSlicedMicroFrontendRoutes.loans,
              `/loan_stress_report/${(response.payload as NotificationPlatformPayload).loan_stress_report_id}`
            )
            window.location.reload()
          }
        }
      }

      if (response.payload && clickableNotifications.includes(response.template_id)) {
        notification.open({
          message: notificationMessage(response),
          description: (
            <Col>
              <Space direction="vertical" size="large" className="cpm-full-width">
                <Row>
                  {(response.payload as NotificationPayloadBase)?.alert_description ||
                    t(
                      [`notifications.template.${response.template_id}.body`, ""],
                      response.payload as unknown as {}
                    )}
                </Row>
                {!!(response.payload as NotificationPlatformPayload).link && (
                  <Row justify="center">
                    <Button type="primary" onClick={onLinkClick}>
                      {t("shared.download")}
                    </Button>
                  </Row>
                )}
              </Space>
            </Col>
          ),
          duration: 0,
          top: NOTIFICATION_MARGIN_TOP,
          onClose: () => {
            onCloseCallback(response)
            PubSub.publish(EVENTS.MA_CRE_CPM_NOTIFICATION_READ, response)
          },
          ...((response.payload as NotificationPlatformPayload).link ||
          clickableNotifications.includes(response.template_id)
            ? {
                onClick: onLinkClick,
                style: { cursor: "pointer" }
              }
            : {
                onClick: (): void => {
                  onClickCallback(response)
                  PubSub.publish(EVENTS.MA_CRE_CPM_NOTIFICATION_READ, response)
                }
              })
        })
      }
    }
    if (!["development"].includes(process.env.REACT_APP_ENV || "")) {
      authApi.connectToNotificationService("UserNotificationChannel", userId, onReceived)
    }
    // eslint-disable-next-line
  }, [cookies])

  return <>{children}</>
}

export default withLanguage(WithNotifications)
